// @ts-strict-ignore
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  NgZone,
  OnDestroy, OnInit,
  ViewChild
} from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { SohoContextMenuDirective } from 'ids-enterprise-ng';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector:    '[lm-field-options]',
  standalone: false,
  templateUrl: './lm-field-options.component.html',
  styleUrls:   [ 'lm-field-options.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LMFieldOptionsComponent implements OnInit, OnDestroy {

  @ViewChild(SohoContextMenuDirective) contextmenu: SohoContextMenuDirective;
  @ViewChild('FieldOptionsButton', { read: ElementRef }) fieldOptionsButton: ElementRef<HTMLElement>;

  @Input() ajaxCallbackFunction: SohoPopupMenuSourceFunction;
  @Input() contextMenuEventFunction: Function;
  @Input() hasContextMenu: boolean;
  @Input() lmParagraphLayoutInfo: LMParagraphLayoutInfo;
  @Input() dataEntryMode = false;
  @Input() isMemberField = false;
  @Input() isCheckBoxField = true;
  @Input() buttonId: string;
  @Input() fieldAutomationId: string;
  @Input() fieldOptionsLabel: string;
  @Input() isInAccessibilityMode: boolean;
  @Input() displayFieldOptions = false;
  @Input() disableFieldOptions = false;

  public showFieldOptions = false;
  private fieldOptionsClicked = false;
  public overrideTabbability = false;

  // soho-context-menu (sohoxi.js PopupMenu, see keydown.popupmenu handler) closes
  // the menu on Tab by calling self.close(true), which internally calls this.element.focus() to put
  // focus back on the trigger button - but it never calls event.preventDefault() on the Tab keydown.
  // Because the browser's native "move focus to next tabbable element" is the DEFAULT ACTION of a Tab
  // keydown, it still runs immediately after all keydown handlers finish, which yanks focus off the
  // button we were just given, regardless of tabindex. We capture Tab ourselves, ahead of the
  // library's own document-level bubble-phase listener, and preventDefault() it (without stopping
  // propagation) so the library still closes the menu normally, but the browser's default focus-move
  // for that specific keypress never happens - leaving focus on the button as the library intended.
  // Active for the entire time the menu is open (registered in openFieldOptions, removed on close/destroy),
  // not just "during close" - named accordingly.
  private readonly blockTabDefaultWhileMenuOpen = (event: KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
    }
  };

  @HostBinding('style.position') hostPosition = 'relative';
  @HostBinding('style.display') hostDisplay = 'inline-flex';
  @HostBinding('class.lm-field-options') lmFieldOptionsClass = true;

  private destroy$ = new Subject<void>();

  constructor(
    private el: ElementRef,
    private changeDetectorRef: ChangeDetectorRef,
    private zone: NgZone) {
    const element = el.nativeElement;
    const mouseEnter$ = fromEvent(element, 'mouseenter', { passive: true });
    const mouseLeave$ = fromEvent(element, 'mouseleave', { passive: true });

    mouseEnter$.pipe(
      tap(() => {
        this.showFieldOptions = true;
        this.changeDetectorRef.markForCheck();
      }),
      takeUntil(this.destroy$)
    ).subscribe();

    mouseLeave$.pipe(
      tap(() => {
        if (this.fieldOptionsClicked) {
          // if the field options has been clicked, leave the button visible for a bit
          this.zone.runOutsideAngular(() => {
            setTimeout(() => {
              this.showFieldOptions = false;
              this.fieldOptionsClicked = false;
              this.changeDetectorRef.markForCheck();
            }, 100);
          });
        } else {
          this.showFieldOptions = false;
          this.changeDetectorRef.markForCheck();
        }
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  public ngOnInit() {
    if ((this.lmParagraphLayoutInfo && this.lmParagraphLayoutInfo.textAreaOnly) || this.isCheckBoxField) {
      this.hostDisplay = '';
    }
  }

  public ngOnDestroy() {
    document.removeEventListener('keydown', this.blockTabDefaultWhileMenuOpen, true);
    this.destroy$.next();
  }
  handleFocus() {
    this.fieldOptionsButton?.nativeElement?.classList.remove('hide-focus');
  }

  public openFieldOptions(event: Event) {
    if (event) {
      event.stopImmediatePropagation();
      event.stopPropagation();
      event.preventDefault();
    }
    this.fieldOptionsClicked = true;
    this.showFieldOptions = true;
    this.contextmenu.initializeComponent();
  }

  // Registered on the SohoContextMenuDirective's (open) output rather than in openFieldOptions(),
  // so the Tab-blocking listener is only attached once the menu has actually opened - avoiding a
  // leaked listener if beforeOpen/the ajax callback never resolves or throws.
  public onFieldOptionsContextMenuOpen() {
    document.addEventListener('keydown', this.blockTabDefaultWhileMenuOpen, true);
  }

  public onFieldOptionsContextMenuClose() {
    this.showFieldOptions = true;
    this.fieldOptionsClicked = false;
    document.removeEventListener('keydown', this.blockTabDefaultWhileMenuOpen, true);
    // LMCLIENT-38107 on modals, we need to make the button tabbable to not mess up the tab order.
    if (!this.isInAccessibilityMode) {
      this.overrideTabbability = true;
    }
    this.changeDetectorRef.detectChanges();
  }

  handleBlur() {
    if (!this.isInAccessibilityMode) {
      this.overrideTabbability = false;
    }
    if (!this.fieldOptionsClicked) {
      this.showFieldOptions = false;
    }
    this.changeDetectorRef.markForCheck();
  }
}
